import "@/index.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AppProvider, useApp } from "@/context/AppContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { ResolvedConfigProvider } from "@/context/ResolvedConfigContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";
import { Toaster } from "@/components/ui/sonner";

import Login from "@/pages/Login";
import Workflow from "@/pages/Workflow";
import WeeklyMenu from "@/pages/WeeklyMenu";
import Recipes from "@/pages/Recipes";
import Inventory from "@/pages/Inventory";
import Deliveries from "@/pages/Deliveries";
import Suppliers from "@/pages/Suppliers";
import DailyChecks from "@/pages/DailyChecks";
import Reports from "@/pages/Reports";
import Announcements from "@/pages/Announcements";
import Settings from "@/pages/Settings";
import SearchPage from "@/pages/Search";
import AdminPortal from "@/pages/AdminPortal";
import Onboarding from "@/pages/Onboarding";
import OnboardingGate from "@/components/OnboardingGate";
import LegalAccept from "@/pages/LegalAccept";
import LegalGate from "@/components/LegalGate";
import ProductionReview from "@/pages/ProductionReview";
import Companion from "@/pages/Companion";
import Profile from "@/pages/Profile";
// Prompt 10A — Workforce frontend shell (module context; no data yet).
import WorkforceRouteGuard from "@/components/workforce/WorkforceRouteGuard";
import WorkforceLayout from "@/components/workforce/WorkforceLayout";
import WorkforceSchedule from "@/pages/workforce/WorkforceSchedule";
import WorkforceTeam from "@/pages/workforce/WorkforceTeam";
import WorkforceReviewHours from "@/pages/workforce/WorkforceReviewHours";
// Prompt 15B — Employee Mobile: public standalone route, NEVER
// nested under Layout / WorkforceLayout / manager Legal + Onboarding
// gates. Employees do not carry manager cookies (§3 · §6).
import ExternalShiftReport from "@/pages/ExternalShiftReport";
import WorkforceExternalExtras from "@/pages/workforce/WorkforceExternalExtras";
import EmployeeWorkforceMobile
    from "@/pages/workforce/EmployeeWorkforceMobile";
import PlatformUsers from "@/pages/PlatformUsers";
import AdminCompanyDetail from "@/pages/AdminCompanyDetail";
import ProductUnitReview from "@/pages/ProductUnitReview";

// Picks the right landing page based on role. Super admins land on the
// platform portal; everyone else continues to land on Workflow.
function HomeRedirect() {
    const { user } = useApp();
    if (!user) return null;
    return <Navigate to={user.role === "super_admin" ? "/admin" : "/workflow"} replace />;
}

export default function App() {
    // Global numeric input UX fix (Prompt 30 §16):
    //   When a user focuses a number input (or an input marked with the
    //   `data-numeric-select` attribute), select the entire current value
    //   so the next keystroke replaces it cleanly. This fixes the case
    //   where a "0" or previous value stays anchored ahead of new input.
    //   Also strips a leading "0" when the user types a real digit.
    useEffect(() => {
        const isNumeric = (el) => {
            if (!(el instanceof HTMLInputElement)) return false;
            const t = (el.getAttribute("type") || "").toLowerCase();
            return t === "number" || el.hasAttribute("data-numeric-select");
        };
        const onFocusIn = (e) => {
            const el = e.target;
            if (!isNumeric(el)) return;
            // Defer so the focus rendering settles first.
            requestAnimationFrame(() => {
                try { el.select(); } catch { /* some UAs disallow select() on number inputs — ignore */ }
            });
        };
        const onKeyDown = (e) => {
            const el = e.target;
            if (!isNumeric(el)) return;
            // If value is "0" and the user starts typing a real digit,
            // wipe the field so the new digit doesn't become "05" or "01".
            if (el.value === "0" && /^[0-9]$/.test(e.key)) {
                el.value = "";
            }
        };
        document.addEventListener("focusin", onFocusIn, true);
        document.addEventListener("keydown", onKeyDown, true);
        return () => {
            document.removeEventListener("focusin", onFocusIn, true);
            document.removeEventListener("keydown", onKeyDown, true);
        };
    }, []);

    return (
        <AppProvider>
            <ThemeProvider>
                <ResolvedConfigProvider>
                <BrowserRouter>
                <Routes>
                    <Route path="/login" element={<Login />} />
                    <Route path="/companion" element={<Companion />} />
                    {/* Prompt 15B §3 — public Employee Mobile route.
                        Rendered OUTSIDE ProtectedRoute / Layout /
                        WorkforceLayout so the manager sidebar and
                        manager auth cookie never touch this surface. */}
                    <Route
                        path="/employee/workforce"
                        element={<EmployeeWorkforceMobile />}
                    />
                    {/* WORKFORCE-EXTERNAL-EXTRAS-QR-V1 — public shift report
                        opened from the restaurant QR; no manager auth. */}
                    <Route path="/shift-report/:token" element={<ExternalShiftReport />} />
                    <Route
                        path="/onboarding"
                        element={<ProtectedRoute><Onboarding /></ProtectedRoute>}
                    />
                    <Route
                        path="/legal/accept"
                        element={<ProtectedRoute><LegalAccept /></ProtectedRoute>}
                    />
                    <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
                        <Route element={<LegalGate />}>
                        <Route element={<OnboardingGate />}>
                            <Route path="/" element={<HomeRedirect />} />
                            <Route path="/workflow" element={<Workflow />} />
                            <Route path="/weekly-menu" element={<WeeklyMenu />} />
                            <Route path="/recipes" element={<Recipes />} />
                            <Route path="/inventory" element={<Inventory />} />
                            <Route path="/deliveries" element={<Deliveries />} />
                            <Route path="/suppliers" element={<Suppliers />} />
                            <Route path="/daily-checks" element={<DailyChecks />} />
                            <Route path="/production-review" element={<ProductionReview />} />
                            <Route path="/reports" element={<Reports />} />
                            <Route path="/announcements" element={<Announcements />} />
                            <Route path="/settings" element={<Settings />} />
                            <Route path="/settings/unit-review" element={<ProductUnitReview />} />
                            <Route path="/search" element={<SearchPage />} />
                            <Route path="/admin" element={<AdminPortal />} />
                            <Route path="/admin/companies/:companyId" element={<AdminCompanyDetail />} />
                            <Route path="/admin/platform-users" element={<PlatformUsers />} />
                            <Route path="/me" element={<Profile />} />
                            {/* Prompt 10A — Workforce shell.
                                Route guard enforces role + module state
                                + active restaurantId; else redirect
                                to "/" (existing HomeRedirect resolves
                                the correct landing). Backend remains
                                authoritative. */}
                            <Route element={<WorkforceRouteGuard />}>
                                <Route path="/workforce"
                                       element={<WorkforceLayout />}>
                                    <Route index
                                           element={<WorkforceSchedule />} />
                                    {/* Prompt 13C — Review Hours page.
                                        Owner-authorized route. */}
                                    <Route path="review-hours"
                                           element={<WorkforceReviewHours />} />
                                    <Route path="team"
                                           element={<WorkforceTeam />} />
                                    <Route path="external-extras"
                                           element={<WorkforceExternalExtras />} />
                                </Route>
                            </Route>
                        </Route>
                        </Route>
                    </Route>
                    <Route path="*" element={<HomeRedirect />} />
                </Routes>
            </BrowserRouter>
            <Toaster position="top-right" />
            </ResolvedConfigProvider>
            </ThemeProvider>
        </AppProvider>
    );
}
